import { BaseTimePeriod } from '@services/FiltersTypes';
import { FilterType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import {
  TOP_PAGE_FILTER_TRACKING_TITLE,
  TopPageFilterFilterName,
  TopPageFiltersTrackingFunctionParam,
  topPageFiltersTrackingFunction,
} from '@utils/page-analytics/model/TopPageFiltersTracking';
import { RelativePeriod } from '@components/comparison-period-selector/Types';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { PagesConfig } from '@utils/pageConfig/PagesConfigContext';
import { PageName, PageType } from '@utils/pageConfig/Types';
import { CustomMonthsPeriod, CustomWeeksPeriod } from '@utils/period-set/UseInitializedPeriodSet';
import { CustomMonthsPeriodState, CustomWeeksPeriodState } from '@state/reducers/main-filter/MainFiltersReducer';

interface ApplyParamsTFP {
  givenPeriodicity: Periodicity;
  givenPeriod: BaseTimePeriod;
  givenComparison: RelativePeriod | BaseTimePeriod;
  pageName: PageName;
  pageType: PageType;
  pagesConfig: PagesConfig;
  countryChanged?: boolean;
  sideFiltersState: any;
  retailerClientState: any;
  track: any;
  setTimeFrame: (selectedPeriodicity: Periodicity) => void;
  setPeriod: (selectedOption: BaseTimePeriod) => void;
  setRelativePeriod: (selectedRelativePeriod: RelativePeriod | BaseTimePeriod) => void;
  setCustomMonthPeriodState?: (setCustomMonthPeriodState: CustomMonthsPeriodState) => void;
  setCustomWeekPeriodState?: (setCustomWeekPeriodState: CustomWeeksPeriodState) => void;
  setCountryChanged?: (currentCountryChangedStatus: boolean) => void;
}

/**
 * The apply function callback on selecting apply within the TimeFramePicker. This function should
 * only be used in conjuction with the TimeFramePicker within a page.
 */
export const applyTimeFramePicker = ({
  givenPeriodicity,
  givenPeriod,
  givenComparison,
  pageName,
  pageType,
  track,
  pagesConfig,
  countryChanged,
  sideFiltersState,
  retailerClientState,
  setTimeFrame,
  setPeriod,
  setRelativePeriod,
  setCustomMonthPeriodState,
  setCustomWeekPeriodState,
  setCountryChanged,
}: ApplyParamsTFP) => {
  // Apply selected time frame selections
  setTimeFrame(givenPeriodicity);
  setPeriod(givenPeriod);
  setRelativePeriod(givenComparison);

  //setCustomMonthPeriodSet is optional because not all page require to update Redux value for Custom Month, only PA necessary for now
  //Only set redux if only selection is custom months, else custom months default value will be populated
  if (Periodicity.CustomMonths === givenPeriodicity && setCustomMonthPeriodState) {
    const customMonthsPeriod: CustomMonthsPeriod = {
      period: givenPeriod,
      comparisonPeriod: givenComparison as BaseTimePeriod,
    };
    setCustomMonthPeriodState(customMonthsPeriod);
  }
  if (Periodicity.CustomWeeks === givenPeriodicity && setCustomWeekPeriodState) {
    const customWeeksPeriod: CustomWeeksPeriod = {
      period: givenPeriod,
      comparisonPeriod: givenComparison as BaseTimePeriod,
    };
    setCustomWeekPeriodState(customWeeksPeriod);
  }

  if (countryChanged && setCountryChanged) {
    setCountryChanged(false);
  }
  pagesConfig.setIsTimePeriodFirstLoad(false);

  const trackedGivenComparison = [Periodicity.CustomMonths, Periodicity.CustomWeeks].includes(givenPeriodicity)
    ? (givenComparison as BaseTimePeriod).label
    : (givenComparison as RelativePeriod);

  // Start amplitude tracking
  const tracker: TopPageFiltersTrackingFunctionParam = {
    pageName: pageName,
    sideFilters: sideFiltersState.appliedFilters,
    retailerClientState: retailerClientState,
    track,
    eventTitle: TOP_PAGE_FILTER_TRACKING_TITLE,
    pageType: pageType,
    filterName: TopPageFilterFilterName.TIMEFRAME,
    filterType: FilterType.SELECTORS,
    periodicity: givenPeriodicity,
    focusPeriod: givenPeriod.label,
    comparisonPeriod: trackedGivenComparison,
    access: pagesConfig.access,
  };

  topPageFiltersTrackingFunction(tracker);
};
